<template>
  <div class="oauth-server-root">
    <div class="oauth-server-header">
      <div class="oauth-server-header-text">
        <div class="settings-section-title">OAuth Server</div>
        <div class="settings-section-desc">
          Let this app act as an OAuth provider. External applications can authenticate users against
          a spreadsheet in this app. A login page is generated automatically and is not visible in the app's navigation.
        </div>
      </div>
    </div>

    <!-- Enable toggle -->
    <div class="oauth-server-section">
      <label class="oauth-toggle-row">
        <input type="checkbox" v-model="config.enabled" class="oauth-checkbox" />
        <span class="oauth-toggle-label">Enable OAuth Server</span>
      </label>
    </div>

    <template v-if="config.enabled">
      <!-- User spreadsheet selection -->
      <div class="oauth-server-section">
        <div class="oauth-section-heading">User Spreadsheet</div>
        <div class="oauth-section-hint">Select the spreadsheet that stores your users and map the credential columns.</div>

        <div class="oauth-field-row">
          <label class="oauth-label">Spreadsheet</label>
          <select class="oauth-select" v-model="config.spreadsheetUniqueTitle" @change="onSpreadsheetChange">
            <option value="">-- select a spreadsheet --</option>
            <option
              v-for="ss in spreadsheets"
              :key="ss.uniqueTitle"
              :value="ss.uniqueTitle"
            >{{ ss.title || ss.uniqueTitle }}</option>
          </select>
        </div>

        <template v-if="selectedColumns.length">
          <div class="oauth-field-row">
            <label class="oauth-label">Username / Login Column</label>
            <select class="oauth-select" v-model="config.usernameColumn">
              <option value="">-- select --</option>
              <option v-for="col in selectedColumns" :key="col.uniqueTitle" :value="col.uniqueTitle">
                {{ col.title || col.uniqueTitle }} ({{ col.type }})
              </option>
            </select>
          </div>

          <div class="oauth-field-row">
            <label class="oauth-label">Password Column</label>
            <select class="oauth-select" v-model="config.passwordColumn">
              <option value="">-- select --</option>
              <option v-for="col in passwordColumns" :key="col.uniqueTitle" :value="col.uniqueTitle">
                {{ col.title || col.uniqueTitle }} ({{ col.type }})
              </option>
            </select>
          </div>

          <div class="oauth-field-row">
            <label class="oauth-label">Email Column <span class="oauth-optional">(optional)</span></label>
            <select class="oauth-select" v-model="config.emailColumn">
              <option value="">-- none --</option>
              <option v-for="col in selectedColumns" :key="col.uniqueTitle" :value="col.uniqueTitle">
                {{ col.title || col.uniqueTitle }} ({{ col.type }})
              </option>
            </select>
          </div>

          <div class="oauth-field-row">
            <label class="oauth-label">Display Name Column <span class="oauth-optional">(optional)</span></label>
            <select class="oauth-select" v-model="config.displayNameColumn">
              <option value="">-- none --</option>
              <option v-for="col in selectedColumns" :key="col.uniqueTitle" :value="col.uniqueTitle">
                {{ col.title || col.uniqueTitle }} ({{ col.type }})
              </option>
            </select>
          </div>
        </template>
      </div>

      <!-- Registered Clients -->
      <div class="oauth-server-section">
        <div class="oauth-section-heading-row">
          <div>
            <div class="oauth-section-heading">Registered Clients</div>
            <div class="oauth-section-hint">External apps that are allowed to authenticate through this OAuth server.</div>
          </div>
          <button class="oauth-add-btn" @click="addClient">
            <font-awesome-icon icon="plus" /> add client
          </button>
        </div>

        <div v-if="config.clients.length" class="oauth-client-list">
          <div
            v-for="(client, index) in config.clients"
            :key="client.id"
            class="oauth-client-card"
          >
            <div class="oauth-client-header">
              <button type="button" class="oauth-client-summary" :aria-expanded="clientExpanded[index] ? 'true' : 'false'" @click="toggleClientExpand(index)">
                <font-awesome-icon :icon="clientExpanded[index] ? 'chevron-down' : 'chevron-right'" class="oauth-expand-icon" />
                <span class="oauth-client-name">{{ client.name || 'Unnamed Client' }}</span>
                <span class="oauth-client-id-badge">{{ client.clientId || '...' }}</span>
              </button>
              <button type="button" class="oauth-remove-btn" @click.stop="removeClient(index)" aria-label="Remove client">
                <font-awesome-icon icon="minus" />
              </button>
            </div>

            <div v-if="clientExpanded[index]" class="oauth-client-fields">
              <div class="oauth-field-row">
                <label class="oauth-label">Application Name</label>
                <input class="oauth-input" v-model="client.name" placeholder="My External App" />
              </div>

              <div class="oauth-field-row">
                <label class="oauth-label">Client ID</label>
                <div class="oauth-input-with-btn">
                  <input class="oauth-input" v-model="client.clientId" placeholder="Auto-generated" readonly />
                  <button class="oauth-gen-btn" @click="regenerateClientId(client)" title="Regenerate">
                    <font-awesome-icon icon="sync-alt" />
                  </button>
                </div>
              </div>

              <div class="oauth-field-row">
                <label class="oauth-label">Client Secret</label>
                <div class="oauth-input-with-btn">
                  <input class="oauth-input" v-model="client.clientSecret" placeholder="Auto-generated" readonly />
                  <button class="oauth-gen-btn" @click="regenerateClientSecret(client)" title="Regenerate">
                    <font-awesome-icon icon="sync-alt" />
                  </button>
                </div>
              </div>

              <div class="oauth-field-row">
                <label class="oauth-label">Allowed Redirect URI</label>
                <input class="oauth-input" v-model="client.redirectUri" placeholder="https://external-app.com/oauth/callback" />
              </div>
            </div>
          </div>
        </div>

        <div v-else class="oauth-client-empty">
          No clients registered. Click <strong>+ add client</strong> to allow an external app to authenticate.
        </div>
      </div>

      <!-- Endpoint Reference -->
      <div class="oauth-server-section" v-if="config.enabled && config.spreadsheetUniqueTitle">
        <div class="oauth-section-heading">Endpoint Reference</div>
        <div class="oauth-section-hint">Provide these URLs to external apps integrating with your OAuth server.</div>
        <div class="oauth-endpoints">
          <div class="oauth-endpoint-row">
            <span class="oauth-endpoint-label">Authorization:</span>
            <code class="oauth-endpoint-url">{{ baseUrl }}/{{ account }}/{{ application }}/oauth/server/authorize</code>
          </div>
          <div class="oauth-endpoint-row">
            <span class="oauth-endpoint-label">Token:</span>
            <code class="oauth-endpoint-url">{{ baseUrl }}/{{ account }}/{{ application }}/oauth/server/token</code>
          </div>
          <div class="oauth-endpoint-row">
            <span class="oauth-endpoint-label">User Info:</span>
            <code class="oauth-endpoint-url">{{ baseUrl }}/{{ account }}/{{ application }}/oauth/server/userinfo</code>
          </div>
        </div>
      </div>
    </template>

    <!-- Save button -->
    <div class="oauth-footer">
      <button
        class="save-btn"
        :class="{ 'save-btn-disabled': !hasChanges }"
        :disabled="!hasChanges || saving"
        @click="saveConfig"
      >
        <template v-if="saving">saving...</template>
        <template v-else-if="saved">saved!</template>
        <template v-else>save oauth server config</template>
      </button>
    </div>
  </div>
</template>

<script>

import axios from 'axios'
import { onDesignerChange, raiseDesignerWarning } from '../../lib/designer-sync.js'

function generateId () {
  return Date.now().toString(36) + '_' + Math.random().toString(36).slice(2, 8)
}

function generateSecret (length) {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789'
  let result = ''
  for (let i = 0; i < length; i++) {
    result += chars.charAt(Math.floor(Math.random() * chars.length))
  }
  return result
}

function defaultConfig () {
  return {
    enabled: false,
    spreadsheetUniqueTitle: '',
    usernameColumn: '',
    passwordColumn: '',
    emailColumn: '',
    displayNameColumn: '',
    clients: []
  }
}

export default {
  name: 'SettingsOAuthServer',
  props: ['application'],
  data () {
    return {
      config: defaultConfig(),
      savedSnapshot: '',
      spreadsheets: [],
      clientExpanded: [],
      saving: false,
      saved: false,
      account: 'default',
      baseUrl: VUE_APP_SERVER_URL || VUE_APP_PROXY_SERVER_URL || 'http://localhost:9998'
    }
  },
  computed: {
    hasChanges () {
      return JSON.stringify(this.config) !== this.savedSnapshot
    },
    selectedSpreadsheet () {
      if (!this.config.spreadsheetUniqueTitle) return null
      return this.spreadsheets.find(s => s.uniqueTitle === this.config.spreadsheetUniqueTitle) || null
    },
    selectedColumns () {
      if (!this.selectedSpreadsheet) return []
      return this.selectedSpreadsheet.columns || []
    },
    passwordColumns () {
      return this.selectedColumns.filter(c => c.type === 'password')
    }
  },
  async mounted () {
    await Promise.all([this.loadConfig(), this.loadSpreadsheets()])
    this._stopDesignerSync = onDesignerChange((change) => this.applyDesignerChange(change))
  },
  beforeUnmount () {
    if (this._stopDesignerSync) this._stopDesignerSync()
  },
  watch: {
    async application () {
      await Promise.all([this.loadConfig(), this.loadSpreadsheets()])
    }
  },
  methods: {
    async applyDesignerChange (change) {
      const keys = change && change.keys || []
      if (!keys.includes('settings:oauth-server') && !keys.includes('settings') && !keys.includes('application')) return
      if (this.hasChanges) {
        raiseDesignerWarning(change, keys.includes('application') ? 'this application' : 'the OAuth server')
        return
      }
      await this.loadConfig()
    },
    async loadConfig () {
      try {
        const res = await axios.get(`${VUE_APP_PROXY_SERVER_URL}/settings`, {
          headers: { 'application-unique-title': this.application }
        })

        if (res.data && res.data.oauthServer) {
          this.config = { ...defaultConfig(), ...res.data.oauthServer }
          if (!Array.isArray(this.config.clients)) this.config.clients = []
        } else {
          this.config = defaultConfig()
        }

        this.savedSnapshot = JSON.stringify(this.config)
        this.clientExpanded = this.config.clients.map(() => false)
      } catch (err) {
        console.log('failed to load oauth server config', err)
      }
    },
    async loadSpreadsheets () {
      try {
        const res = await axios.get(`${VUE_APP_PROXY_SERVER_URL}/spreadsheets/`, {
          headers: { 'application-unique-title': this.application }
        })
        this.spreadsheets = res.data || []
      } catch (err) {
        console.log('failed to load spreadsheets', err)
      }
    },
    onSpreadsheetChange () {
      this.config.usernameColumn = ''
      this.config.passwordColumn = ''
      this.config.emailColumn = ''
      this.config.displayNameColumn = ''
    },
    addClient () {
      this.config.clients.push({
        id: generateId(),
        name: '',
        clientId: generateSecret(24),
        clientSecret: generateSecret(48),
        redirectUri: ''
      })
      this.clientExpanded.push(true)
    },
    removeClient (index) {
      if (!confirm('Remove this client?')) return
      this.config.clients.splice(index, 1)
      this.clientExpanded.splice(index, 1)
    },
    toggleClientExpand (index) {
      this.clientExpanded[index] = !this.clientExpanded[index]
      this.clientExpanded = [...this.clientExpanded]
    },
    regenerateClientId (client) {
      if (!confirm('Regenerate client ID? External apps using the old ID will stop working.')) return
      client.clientId = generateSecret(24)
    },
    regenerateClientSecret (client) {
      if (!confirm('Regenerate client secret? External apps using the old secret will stop working.')) return
      client.clientSecret = generateSecret(48)
    },
    async saveConfig () {
      if (!this.hasChanges) return

      this.saving = true
      this.saved = false

      try {
        await axios.put(
          `${VUE_APP_PROXY_SERVER_URL}/settings`,
          { oauthServer: this.config },
          { headers: { 'application-unique-title': this.application } }
        )

        this.savedSnapshot = JSON.stringify(this.config)
        this.saved = true
        setTimeout(() => { this.saved = false }, 2000)
      } catch (err) {
        console.log('failed to save oauth server config', err)
        alert('Failed to save OAuth server config')
      } finally {
        this.saving = false
      }
    }
  }
}
</script>

<style scoped>

.oauth-server-root {
  display: flex;
  flex-direction: column;
  height: calc(100vh - 102px);
  overflow-y: auto;
  padding: 16px 24px;
}

.oauth-server-header {
  margin-bottom: 16px;
}

.settings-section-title {
  font-size: 0.9rem;
  font-weight: 600;
  color: #e8e8e6;
  margin-bottom: 2px;
}

.settings-section-desc {
  font-size: 0.75rem;
  color: #707070;
  max-width: 600px;
}

/* ── Sections ── */
.oauth-server-section {
  margin-bottom: 20px;
  padding-bottom: 16px;
  border-bottom: 1px solid #1e1e1b;
}

.oauth-section-heading {
  font-size: 0.82rem;
  font-weight: 600;
  color: #c0c0be;
  margin-bottom: 2px;
}

.oauth-section-heading-row {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: 10px;
}

.oauth-section-hint {
  font-size: 0.72rem;
  color: #a8a8a4;
  margin-bottom: 10px;
}

/* ── Toggle ── */
.oauth-toggle-row {
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
}

.oauth-checkbox {
  accent-color: #9ad12b;
  width: 16px;
  height: 16px;
}

.oauth-toggle-label {
  font-size: 0.82rem;
  color: #c0c0be;
  font-weight: 500;
}

/* ── Fields ── */
.oauth-field-row {
  display: flex;
  flex-direction: column;
  gap: 3px;
  margin-bottom: 8px;
}

.oauth-label {
  font-size: 0.72rem;
  color: #707070;
  font-weight: 500;
}

.oauth-optional {
  color: #3a3a37;
  font-weight: 400;
}

.oauth-input,
.oauth-select {
  width: 100%;
  max-width: 500px;
  padding: 6px 10px;
  border-radius: 4px;
  border: 1px solid #2a2a27;
  background: #0f0f0d;
  color: #e8e8e6;
  font-size: 0.8rem;
  font-family: inherit;
  outline: none;
  transition: border-color 0.12s;
}

.oauth-input:focus,
.oauth-select:focus {
  border-color: #9ad12b;
}

.oauth-input::placeholder {
  color: #3a3a37;
}

.oauth-input[readonly] {
  color: #909090;
  cursor: default;
}

.oauth-select option {
  background: #0f0f0d;
  color: #e8e8e6;
}

.oauth-input-with-btn {
  display: flex;
  gap: 4px;
  max-width: 500px;
}

.oauth-input-with-btn .oauth-input {
  flex: 1;
  max-width: none;
}

.oauth-gen-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  border-radius: 4px;
  border: 1px solid #2a2a27;
  background: transparent;
  color: #a8a8a4;
  font-size: 0.7rem;
  cursor: pointer;
  transition: color 0.12s, border-color 0.12s;
  flex-shrink: 0;
}

.oauth-gen-btn:hover {
  color: #9ad12b;
  border-color: #9ad12b;
}

/* ── Client list ── */
.oauth-add-btn {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 14px;
  border-radius: 5px;
  border: 1px solid #3a3a37;
  background: transparent;
  color: #a0a09e;
  font-size: 0.78rem;
  cursor: pointer;
  transition: border-color 0.12s, color 0.12s, background 0.12s;
  flex-shrink: 0;
}

.oauth-add-btn:hover {
  border-color: #9ad12b;
  color: #e8e8e6;
  background: rgba(154, 209, 43, 0.06);
}

.oauth-client-list {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.oauth-client-card {
  border: 1px solid #2a2a27;
  border-radius: 6px;
  background: #141412;
  overflow: hidden;
}

.oauth-client-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 14px;
  transition: background 0.1s;
}

.oauth-client-header:hover {
  background: #1a1a17;
}

.oauth-client-summary {
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
  min-width: 0;
  border: 0;
  background: transparent;
  color: inherit;
  font: inherit;
  text-align: left;
  padding: 0;
  cursor: pointer;
}

.oauth-expand-icon {
  font-size: 0.65rem;
  color: #a8a8a4;
  width: 10px;
}

.oauth-client-name {
  font-size: 0.82rem;
  color: #c0c0be;
}

.oauth-client-id-badge {
  font-size: 0.62rem;
  color: #a8a8a4;
  background: #1a1a17;
  border: 1px solid #2a2a27;
  padding: 1px 6px;
  border-radius: 3px;
  font-family: monospace;
}

.oauth-remove-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  border-radius: 4px;
  border: 1px solid transparent;
  background: transparent;
  color: #a8a8a4;
  font-size: 0.7rem;
  cursor: pointer;
  transition: color 0.12s, background 0.12s, border-color 0.12s;
}

.oauth-remove-btn:hover {
  color: #e05050;
  background: rgba(224, 80, 80, 0.08);
  border-color: rgba(224, 80, 80, 0.2);
}

.oauth-client-fields {
  padding: 4px 14px 14px;
  border-top: 1px solid #2a2a27;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.oauth-client-empty {
  text-align: center;
  padding: 24px 20px;
  color: #a8a8a4;
  font-size: 0.8rem;
}

.oauth-client-empty strong {
  color: #707070;
}

/* ── Endpoints ── */
.oauth-endpoints {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.oauth-endpoint-row {
  display: flex;
  align-items: center;
  gap: 8px;
}

.oauth-endpoint-label {
  font-size: 0.72rem;
  font-weight: 600;
  color: #707070;
  min-width: 100px;
}

.oauth-endpoint-url {
  font-size: 0.72rem;
  color: #9ad12b;
  background: #0f0f0d;
  padding: 3px 8px;
  border-radius: 3px;
  border: 1px solid #2a2a27;
  word-break: break-all;
}

/* ── Footer ── */
.oauth-footer {
  margin-top: 16px;
  max-width: 500px;
}

.save-btn {
  width: 100%;
  padding: 7px 0;
  border-radius: 5px;
  border: none;
  background-color: #9ad12b;
  color: #0f0f0d;
  font-size: 0.8rem;
  font-weight: 600;
  cursor: pointer;
  transition: opacity 0.12s, background 0.12s;
}

.save-btn:hover {
  background-color: #b0e840;
}

.save-btn-disabled {
  opacity: 0.35;
  cursor: default;
  background-color: #3a3a37;
  color: #707070;
}

.save-btn-disabled:hover {
  background-color: #3a3a37;
}

</style>
